import React, { useState } from "react";
import { DeleteOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { Badge, Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, type TableProps } from "antd";
import dayjs from "dayjs";
import isEqual from "lodash.isequal";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  ExerciseRecordingStatus,
  type ExerciseRecordingWithExerciseInfo,
} from "@fitness-app/data-models/entities/ExerciseRecording";
import { type TraineeWithAssignedTrainerAndProduct } from "@fitness-app/data-models/entities/Trainee";
import { getAuthorName } from "@fitness-app/data-models/utils/getAuthorName";

import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import VideoThumbnailButton from "~/components/VideoPlayer/VideoThumbnailButton";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { createRecordingStatusOptions } from "~/modules/Trainee/helpers/createStatusOptions";
import { type ExerciseRecordingWithManualWorkoutInfo } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useTraineeExerciseRecordings";

interface TraineeVideoRecordingsTableProps {
  data: ExerciseRecordingWithManualWorkoutInfo[];
  isLoading: boolean;
  withTraineeInfo?: boolean;
  onAddFeedback?: (recording: ExerciseRecordingWithExerciseInfo) => void;
  onUploadVideo?: (recording: ExerciseRecordingWithExerciseInfo) => void;
  onDeleteVideo?: (recording: ExerciseRecordingWithExerciseInfo) => void;
  onArchiveRecording?: (recording: ExerciseRecordingWithExerciseInfo) => void;
  statusFilter?: ExerciseRecordingStatus[];
  changeStatusFilter?: (status: ExerciseRecordingStatus[]) => void;
}

export const TraineeVideoRecordingsTable = ({
  data,
  isLoading,
  withTraineeInfo,
  onAddFeedback,
  onUploadVideo,
  onDeleteVideo,
  onArchiveRecording,
  statusFilter,
  changeStatusFilter,
}: TraineeVideoRecordingsTableProps): React.ReactElement => {
  const { t } = useTranslation(["trainees", "common"]);
  const [videoLink, setVideoLink] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isTrainer } = useUserRole();

  const handleChange: TableProps<ExerciseRecordingWithManualWorkoutInfo>["onChange"] = (_pagination, filters) => {
    if (changeStatusFilter && !isEqual(statusFilter, filters.status)) {
      changeStatusFilter(filters.status as ExerciseRecordingStatus[]);
    }
  };

  const archiveRecordingConfirmTitle = t(
    "measurements.archiveRecordingConfirm",
    "Czy na pewno chcesz zarchiwizować zaplanowane nagranie?",
  );
  const archiveRecordingTooltip = t("measurements.archiveRecording", "Archiwizuj zaplanowane nagranie");
  const uploadVideoTooltip = t("measurements.uploadVideo");

  return (
    <>
      <Table<ExerciseRecordingWithManualWorkoutInfo>
        rowKey="id"
        scroll={{ x: true }}
        loading={isLoading}
        dataSource={data}
        onChange={changeStatusFilter ? handleChange : undefined}
        pagination={false}
        locale={{
          emptyText: t("measurements.emptyState"),
        }}
      >
        {isTrainer && (
          <>
            <Table.Column
              key="id"
              dataIndex="id"
              title="ID"
              ellipsis
              render={(id: string) => (
                <Typography.Paragraph
                  copyable={{ tooltips: [id, t("common:copied")], text: id }}
                  style={{ marginBottom: 0 }}
                >
                  {truncate(id, { length: 10 })}
                </Typography.Paragraph>
              )}
            />
          </>
        )}
        {withTraineeInfo && (
          <>
            <Table.Column<ExerciseRecordingWithExerciseInfo>
              title={t("tableHeader.name")}
              dataIndex="name"
              key="name"
              render={(_, row) => (
                <Button
                  type="link"
                  className="px-0"
                  onClick={() => navigate(`/trainee/details/${row.traineeId}?tab=measurements&subTab=videos`)}
                >
                  {row.trainee?.firstName || "-"} {row.trainee?.lastName}
                </Button>
              )}
            />
            <Table.Column<ExerciseRecordingWithExerciseInfo>
              title={t("tableHeader.email")}
              dataIndex="email"
              ellipsis
              render={(_, row) => {
                if (row.trainee) {
                  return (
                    <Typography.Link
                      onClick={() => navigate(`/trainee/details/${row.traineeId}?tab=measurements&subTab=videos`)}
                      copyable
                    >
                      {row.trainee.email}
                    </Typography.Link>
                  );
                }
                return "-";
              }}
            />
          </>
        )}

        <Table.Column<ExerciseRecordingWithExerciseInfo>
          key="scheduledAt"
          dataIndex="scheduledAt"
          title={t("measurements.workoutDate")}
          render={(date: string) => dayjs(date).format("DD.MM.YYYY")}
        />

        <Table.Column<ExerciseRecordingWithExerciseInfo>
          key="addedtAt"
          dataIndex="addedAt"
          title={t("measurements.addedAt")}
          render={(_, row) => (row.metadata?.addedAt ? dayjs(row.metadata.addedAt).format("DD.MM.YYYY") : "-")}
        />

        <Table.Column<ExerciseRecordingWithExerciseInfo>
          key="exerciseName"
          dataIndex="exercise"
          title={t("measurements.exerciseName")}
          render={(_, row) => row.exercise.name}
        />

        <Table.Column<ExerciseRecordingWithExerciseInfo>
          key="planName"
          dataIndex="planName"
          title={t("measurements.planName")}
          render={(_, row) => row.plan?.name || "-"}
        />
        <Table.Column<ExerciseRecordingWithManualWorkoutInfo>
          title={t("measurements.link")}
          dataIndex="url"
          key="url"
          render={(url, row) => {
            if (url) {
              return (
                <Space>
                  <VideoThumbnailButton
                    onClick={() => setVideoLink(url)}
                    thumbUrl={row.metadata?.thumbnailUrl}
                    durationSeconds={row.metadata?.videoProbe?.durationSeconds}
                    label={t("measurements.playVideo")}
                  />
                  {onDeleteVideo && (
                    <Popconfirm
                      title={t("measurements.deleteVideoConfirm")}
                      onConfirm={() => onDeleteVideo(row)}
                      okText={t("common:button.delete")}
                      cancelText={t("common:button.cancel")}
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title={t("measurements.deleteVideo")}>
                        <DeleteOutlined className="cursor-pointer text-red-500" style={{ fontSize: 18 }} />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </Space>
              );
            }
            const canArchiveRecording = row.status === ExerciseRecordingStatus.SCHEDULED && row.isManualWorkout;

            if (onUploadVideo || (onArchiveRecording && canArchiveRecording)) {
              return (
                <Space>
                  {onUploadVideo && (
                    <Tooltip title={uploadVideoTooltip}>
                      <Button type="text" size="small" icon={<UploadOutlined />} onClick={() => onUploadVideo(row)} />
                    </Tooltip>
                  )}
                  {onArchiveRecording && canArchiveRecording && (
                    <Popconfirm
                      title={archiveRecordingConfirmTitle}
                      onConfirm={() => onArchiveRecording(row)}
                      okText={t("common:button.archive")}
                      cancelText={t("common:button.cancel")}
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title={archiveRecordingTooltip}>
                        <Button type="text" size="small" danger icon={<InboxOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </Space>
              );
            }
            return "-";
          }}
        />
        <Table.Column<ExerciseRecordingWithExerciseInfo>
          title={t("measurements.status")}
          dataIndex="status"
          key="status"
          {...(statusFilter ? { filteredValue: statusFilter, filters: createRecordingStatusOptions(t) } : {})}
          render={(status: ExerciseRecordingWithExerciseInfo["status"]) => {
            if (status === ExerciseRecordingStatus.RECORDED) {
              return (
                <Badge
                  className="whitespace-nowrap"
                  status="processing"
                  text={t(`exerciseRecordingStatus.${status}`)}
                />
              );
            }

            if (status === ExerciseRecordingStatus.RATED) {
              return (
                <Badge className="whitespace-nowrap" status="success" text={t(`exerciseRecordingStatus.${status}`)} />
              );
            }

            return (
              <Badge className="whitespace-nowrap" status="default" text={t(`exerciseRecordingStatus.${status}`)} />
            );
          }}
        />
        {isTrainer && withTraineeInfo ? (
          <Table.Column<TraineeWithAssignedTrainerAndProduct>
            title={t("tableHeader.trainer")}
            dataIndex={["trainee", "trainer"]}
            key="assignedTrainer"
            render={(trainer?: { firstName: string; lastName: string; userId: string }) =>
              trainer ? <Tag color="magenta">{getAuthorName(trainer)}</Tag> : "-"
            }
          />
        ) : null}

        {onAddFeedback && (
          <Table.Column<ExerciseRecordingWithExerciseInfo>
            title={t("measurements.feedback")}
            dataIndex="feedback"
            key="feedback"
            render={(feedback: ExerciseRecordingWithExerciseInfo["status"], row) => {
              if (!feedback && row.status === ExerciseRecordingStatus.RECORDED) {
                return (
                  <Button size="small" type="primary" onClick={() => onAddFeedback(row)}>
                    {t("measurements.addFeedback")}
                  </Button>
                );
              }

              if (row.feedback) {
                return (
                  <Tooltip title={row.feedback.content}>
                    <Tag>{dayjs(row.feedback.createdAt).format("DD.MM.YYYY HH:mm")}</Tag>
                  </Tooltip>
                );
              }
              return "-";
            }}
          />
        )}
      </Table>
      <VideoPlayer visible={!!videoLink} videoUrl={videoLink} onClose={() => setVideoLink(null)} />
    </>
  );
};
